import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import uuid from 'uuid';

import { CAROUSEL_PAGINATION_LIMIT, PAGINATION_TYPES } from './metadata';
import VideoItem from './video-item';
import VideoItemCarousel from './video-item-carousel';
import VideoPlayer from './video-player';
import PagerWidget from 'widgets/pager-widget/pager-widget';
import { Swiper, SwiperSlide } from 'swiper/react';

import styles from './video-list.scss';
import 'swiper/swiper-bundle.css';
import arrow_left from './media/arrow_left.svg';
import arrow_right from './media/arrow_right.svg';

const VideoList = (props) => {
    const {
        settings,
        data,
        currentVideo,
        VideoListTitle,
        CallToAction,
        handleCTAChange = () => {},
        componentId,
        columnSize,
        handleChangeTitle = () => {},
        showVideo,
        playerClass,
        callsign,
        videoClose,
        currentPage,
        isLoadMoreEnabled,
        currentSite,
        handleSelectSeasonFilter,
        selectedSeasonFilter,
        videos,
        setCurrentPage,
        pageCount, // Prop used in handleArrowClick
        resetSwiper,
        setResetSwiper
    } = props;

    const mainVideo = useRef();
    const swiperRef = useRef();
    const swiperInstance = useRef(null);

    // 1. Ref to hold the latest props
    const latestProps = useRef({});
    latestProps.current = props;

    const [showArrowNext, setShowArrowNext] = useState('');
    const [showArrowPrev, setShowArrowPrev] = useState('hidden');
    const [loadPage, setLoadPage] = useState(1);
    const [swiperKey, setSwiperKey] = useState(0);

    // Ref to track previous videos for change detection without circular deps
    const prevVideosRef = useRef(videos);

    // Handle swiper reset when resetSwiper prop changes
    useEffect(() => {
        if (swiperInstance.current && resetSwiper) {
            setResetSwiper(false);
            setCurrentPage(1);
            setSwiperKey((prev) => prev + 1);
            setShowArrowPrev('hidden');
            setShowArrowNext('');
            setLoadPage(1);
        }
    }, [resetSwiper, setCurrentPage, setResetSwiper]);

    // Handle carousel page loading when loadPage advances.
    // Skip while a reset is pending: on reset `currentPage` is set back to 1 in
    // the same render where `loadPage` is still stale (e.g. from a prior swipe),
    // so `loadPage > currentPage` would wrongly advance the page and trigger an
    // extra "append" fetch that mixes videos across the old/new state.
    useEffect(() => {
        if (
            !resetSwiper &&
            loadPage > currentPage &&
            settings.paginationType === PAGINATION_TYPES.CAROUSEL
        ) {
            setCurrentPage(currentPage + 1);
        }
    }, [loadPage, currentPage, settings.paginationType, setCurrentPage, resetSwiper]);

    useEffect(() => {
        const { pageCount: latestPageCount, currentPage: latestCurrentPage } = latestProps.current;
        // This runs after swiperKey changes AND the new Swiper instance has been set by onSwiper.
        if (swiperInstance.current && swiperKey > 0) {
            let next = '';
            let prev = '';
            if (
                (swiperInstance.current.isEnd ||
                    (swiperInstance.current.slides &&
                        swiperInstance.current.slides.length -
                            swiperInstance.current.activeIndex ===
                            2)) &&
                latestPageCount === latestCurrentPage // Use latestPageCount
            ) {
                next = 'hidden';
            }
            if (swiperInstance.current.isBeginning) {
                prev = 'hidden';
            }
            setShowArrowPrev(prev);
            setShowArrowNext(next);
        }
    }, [props]);

    // Reset loadPage when videos change in non-carousel mode (using ref to avoid circular deps)
    useEffect(() => {
        if (
            prevVideosRef.current !== videos &&
            settings.paginationType !== PAGINATION_TYPES.CAROUSEL
        ) {
            setLoadPage(1);
        }
        prevVideosRef.current = videos;
    }, [videos, settings.paginationType]);

    const loadMoreClick = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            setCurrentPage(currentPage + 1);
        },
        [setCurrentPage, currentPage]
    );

    const goToNext = useCallback(
        (e) => {
            e.stopPropagation();
            setCurrentPage(currentPage + 1);
        },
        [setCurrentPage, currentPage]
    );

    const goToPrevious = useCallback(
        (e) => {
            e.stopPropagation();
            setCurrentPage(currentPage - 1);
        },
        [setCurrentPage, currentPage]
    );

    const handlePageChanged = useCallback(
        (page) => {
            const oneBasedPage = page.selected + 1;
            setCurrentPage(oneBasedPage);
            mainVideo.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
                inline: 'center'
            });
        },
        [setCurrentPage]
    );

    const handleVideoClick = useCallback(
        (e, video) => {
            props.videoClick(e, video);
            mainVideo.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'center'
            });
            // Move focus to the video player after the scroll
            const player = document.querySelector('.responsive-player-wrapper');
            if (player) {
                player.setAttribute('tabindex', '0');
                player.focus();
            }
        },
        [props.videoClick]
    );

    const handleArrowClick = (instance) => {
        const { pageCount, currentPage } = latestProps.current;
        let next = '';
        let prev = '';
        let page = currentPage;

        if (
            instance.slides.length - instance.activeIndex < CAROUSEL_PAGINATION_LIMIT &&
            pageCount > currentPage // Use latestPageCount
        ) {
            page = currentPage + 1;
        }

        if (
            (instance.isEnd || instance.slides.length - instance.activeIndex === 2) &&
            pageCount === currentPage // Use latestPageCount
        ) {
            next = 'hidden';
        }
        if (instance.isBeginning) {
            prev = 'hidden';
        }

        setShowArrowPrev(prev);
        setShowArrowNext(next);
        setLoadPage(page);
    };

    const displayVideoListTitle = settings.displayVideoListTitle ?? true;
    const selectedShows = settings.selectedShows;
    const displayCarousel = settings.paginationType === PAGINATION_TYPES.CAROUSEL;
    const displayPagination =
        settings.paginationType === PAGINATION_TYPES.PAGINATION && pageCount > 1;
    const displayLoadMore =
        settings.paginationType === PAGINATION_TYPES.LOAD_MORE && currentPage < pageCount;
    const currentVideoId = currentVideo && currentVideo.id;
    const currentShowId = selectedShows && selectedShows.length && selectedShows[0].id;
    const zeroBasedCurrentPage = Math.min(Math.max(0, currentPage - 1), pageCount);
    const pillButtonComponent = currentSite?.isPillButtonAvailable ? 'pill-button-component' : '';
    let cta = data?.cta[currentVideoId];
    let showCta = data?.cta[currentShowId];

    return (
        <div className={`video-list ${styles.videoList} column-${columnSize}`}>
            {displayVideoListTitle && (
                <VideoListTitle {...props} id={componentId} handleChangeTitle={handleChangeTitle} />
            )}
            {!!settings.selectedShows.length && (
                <VideoPlayer
                    {...props}
                    API={props.API}
                    settings={settings}
                    playerSettings={settings.player}
                    currentVideo={currentVideo}
                    cta={cta}
                    showCta={showCta}
                    showId={currentShowId}
                    handleCTAChange={handleCTAChange}
                    componentId={componentId}
                    CallToAction={CallToAction}
                    showVideo={showVideo}
                    playerClass={playerClass}
                    videoClose={videoClose}
                    reference={mainVideo}
                    callsign={callsign}
                    isPillButtonAvailable={currentSite?.isPillButtonAvailable}
                    handleSelectSeasonFilter={handleSelectSeasonFilter}
                    selectedSeasonFilter={selectedSeasonFilter}
                />
            )}

            {displayCarousel && (
                <Swiper
                    key={swiperKey}
                    keyboard={{ enabled: true, onlyInViewport: true }}
                    className={[
                        showArrowPrev === 'hidden' ? 'is-first-page' : '',
                        showArrowNext === 'hidden' ? 'is-last-page' : ''
                    ]
                        .filter(Boolean)
                        .join(' ')}
                    onBeforeInit={(swiper) => {
                        swiperRef.current = swiper;
                    }}
                    onSwiper={(swiper) => {
                        swiperInstance.current = swiper;

                        // Manually attach the focus listener for Swiper 6.5
                        if (swiper.el && swiper.el.addEventListener) {
                            swiper.el.addEventListener('focusin', (e) => {
                                const activeSlide = e.target.closest('.swiper-slide');
                                if (activeSlide) {
                                    const index = Array.from(swiper.slides).indexOf(activeSlide);
                                    swiper.slideTo(index);
                                }
                            });
                        }
                    }}
                    onActiveIndexChange={(instance) => {
                        handleArrowClick(instance);
                    }}
                    slidesPerView={2}
                    slidesPerGroup={1}
                    spaceBetween={5}
                    breakpoints={{
                        768: {
                            slidesPerView: 2.5,
                            slidesPerGroup: 2,
                            spaceBetween: 5
                        },
                        1024: {
                            slidesPerView: 4.6,
                            slidesPerGroup: 4,
                            spaceBetween: 1
                        },
                        1280: {
                            slidesPerView: 4.6,
                            slidesPerGroup: 4,
                            spaceBetween: 10
                        }
                    }}
                    watchSlidesVisibility
                    watchSlidesProgress>
                    {videos.map((video) => {
                        const key = video.id || uuid.v4();
                        const isActive = currentVideo && currentVideo.id === video.id;
                        if (!video) {
                            return null;
                        }
                        return (
                            <SwiperSlide key={key}>
                                <VideoItemCarousel
                                    {...video}
                                    key={key}
                                    id={key}
                                    settings={settings}
                                    isActive={isActive}
                                    handleClick={(e) => handleVideoClick(e, video)}
                                    handleClose={props.handleClose}
                                    componentId={componentId}
                                />
                            </SwiperSlide>
                        );
                    })}
                    <div
                        className="splide__arrows"
                        role="group"
                        aria-label="Video carousel navigation">
                        <button
                            type="button"
                            className={`${showArrowPrev} splide__arrow splide__arrow--prev`}
                            onClick={() => swiperRef?.current?.slidePrev()}
                            aria-label="Previous videos"
                            aria-disabled={showArrowPrev === 'hidden'}
                            tabIndex={showArrowPrev === 'hidden' ? -1 : 0}>
                            <img src={arrow_left} alt="" aria-hidden="true" />
                        </button>
                        <button
                            type="button"
                            className={`${showArrowNext} splide__arrow splide__arrow--next`}
                            onClick={() => swiperRef?.current?.slideNext()}
                            aria-label="Next videos"
                            aria-disabled={showArrowNext === 'hidden'}
                            tabIndex={showArrowNext === 'hidden' ? -1 : 0}>
                            <img src={arrow_right} alt="" aria-hidden="true" />
                        </button>
                    </div>
                </Swiper>
            )}

            {!displayCarousel && (
                <ul className="videos" aria-label="Available videos" role="list">
                    {videos.map((video) => {
                        const key = video.id || uuid.v4();
                        const isActive = currentVideo && currentVideo.id === video.id;
                        if (!video) {
                            return null;
                        }
                        return (
                            <VideoItem
                                {...video}
                                key={key}
                                id={key}
                                settings={settings}
                                isActive={isActive}
                                handleClick={(e) => handleVideoClick(e, video)}
                                handleClose={props.handleClose}
                                componentId={componentId}
                            />
                        );
                    })}
                </ul>
            )}

            {displayPagination && (
                <PagerWidget
                    pageCount={pageCount}
                    currentPage={zeroBasedCurrentPage}
                    goToNext={goToNext}
                    goToPrevious={goToPrevious}
                    handlePageChanged={handlePageChanged}
                />
            )}

            {displayLoadMore && (
                <div className="load-more-wrapper">
                    <button
                        type="button"
                        onClick={loadMoreClick}
                        className={`load-more ${
                            !isLoadMoreEnabled ? 'disabled' : ''
                        } ${pillButtonComponent}`}
                        aria-disabled={!isLoadMoreEnabled}
                        aria-label={`Load more videos, currently showing ${videos.length} videos`}
                        tabIndex={isLoadMoreEnabled ? 0 : -1}>
                        Load more
                    </button>
                </div>
            )}
        </div>
    );
};

VideoList.propTypes = {
    API: PropTypes.object,
    columnSize: PropTypes.number.isRequired,
    componentId: PropTypes.string.isRequired,
    videos: PropTypes.array.isRequired,
    handleCTAChange: PropTypes.func,
    pageCount: PropTypes.number.isRequired,
    CallToAction: PropTypes.func.isRequired,
    videoClick: PropTypes.func.isRequired,
    videoClose: PropTypes.func.isRequired,
    currentVideo: PropTypes.object,
    VideoListTitle: PropTypes.func.isRequired,
    handleChangeTitle: PropTypes.func,
    showVideo: PropTypes.bool.isRequired,
    playerClass: PropTypes.string.isRequired,
    setCurrentPage: PropTypes.func.isRequired,
    currentPage: PropTypes.number.isRequired,
    isLoadMoreEnabled: PropTypes.bool.isRequired,
    settings: PropTypes.shape({
        selectedSeasonFilter: PropTypes.object,
        selectedShows: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string.isRequired,
                title: PropTypes.string.isRequired,
                links: PropTypes.array
            })
        ),
        seasonFilter: PropTypes.bool,
        itemsPerPage: PropTypes.number.isRequired,
        displayVideoTitle: PropTypes.bool.isRequired,
        displayVideoListTitle: PropTypes.bool,
        displayShowTitle: PropTypes.bool.isRequired,
        displayVideoDescription: PropTypes.bool.isRequired,
        displayVideoMeta: PropTypes.bool.isRequired,
        includePassportVideos: PropTypes.bool.isRequired,
        videoType: PropTypes.string.isRequired,
        paginationType: PropTypes.string.isRequired,
        order: PropTypes.shape({
            value: PropTypes.string.isRequired,
            title: PropTypes.string.isRequired
        }),
        player: PropTypes.shape({
            autoplay: PropTypes.bool.isRequired,
            topbar: PropTypes.bool.isRequired
        })
    }),
    data: PropTypes.shape({
        cta: PropTypes.object.isRequired,
        title: PropTypes.string.isRequired,
        titlePlaceholder: PropTypes.string.isRequired
    }),
    currentSite: PropTypes.object,
    site: PropTypes.object,
    callsign: PropTypes.string,
    changeComponentData: PropTypes.func,
    handleSelectSeasonFilter: PropTypes.func,
    selectedSeasonFilter: PropTypes.object,
    handleClose: PropTypes.func,
    resetSwiper: PropTypes.bool.isRequired,
    setResetSwiper: PropTypes.func
};

export default VideoList;
